import { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import { getUser } from "../../utils/auth";
import resolveImageUrl from "../../utils/imageUtils";
import toast, { Toaster } from "react-hot-toast";
import { AnimatePresence, motion as MOTION } from "framer-motion";
import {
  trackAction,
  collectFeatureChanges,
  collectInspectorText,
  buildClickDetails,
  FEATURE_LABELS,
} from "../../utils/clickTracking";
import { getPropertyUpdateErrorMessage } from "../../utils/propertyErrors";
import DuplicatePt5Modal from "../../components/DuplicatePt5Modal";
import { useDuplicatePt5Retry } from "../../hooks/useDuplicatePt5Retry";
import { getStarredProperties, toggleStarProperty } from "../../utils/starredProperty";

const ITEMS_PER_PAGE = 12;

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};

const PortraitImage = ({ src, alt, className, enablePortrait, enableExif }) => {
  const [rotateDeg, setRotateDeg] = useState(0);

  useEffect(() => {
    setRotateDeg(0);
  }, [src]);

  const handleLoad = (e) => {
    if (!enablePortrait) return;
    const el = e.currentTarget;
    if (!el?.naturalWidth || !el?.naturalHeight) return;
    setRotateDeg(el.naturalWidth > el.naturalHeight ? 90 : 0);
  };

  return (
    <img
      src={src}
      alt={alt}
      onLoad={handleLoad}
      style={{
        ...(enablePortrait && rotateDeg
          ? { transform: `rotate(${rotateDeg}deg) scale(1.35)` }
          : undefined),
        ...(enableExif ? { imageOrientation: "from-image" } : undefined),
      }}
      className={className}
    />
  );
};

const Assigned = () => {
  const location = useLocation();
  const enableExifOrientationFix = location.pathname.includes("/inspector/pending-review");
  const enablePortraitFix = false;
  const searchParams = new URLSearchParams(location.search);
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [autoRotation, setAutoRotation] = useState(0);
  const [rotation, setRotation] = useState(0);
  const displayedRotation = (autoRotation + rotation) % 360;
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [highlightPin, setHighlightPin] = useState("");

  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const highlightPropertyCard = (pin) => {
    const normalized = normalizePin(pin);
    if (!normalized) return;
    setHighlightPin(normalized);
    setTimeout(() => {
      const el = document.querySelector(`[data-pin="${normalized}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const navigate = useNavigate();

  const closeDetailModal = () => {
    const pinToHighlight = selectedProperty?.pin;
    setShowDetailModal(false);
    setRotation(0);
    setAutoRotation(0);
    highlightPropertyCard(pinToHighlight);
    // Clear the pin query parameter from the URL
    navigate({ search: "" }, { replace: true });
  };

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || (rotation === 0 && autoRotation === 0) || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);

    try {
      const totalRotations = Math.abs((rotation + autoRotation) / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.id}`, {
          pin: selectedProperty.pin,
          rotateBy: 90,
          forcePortrait: false
        });
      }

      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      setRotation(0);
      setAutoRotation(0);
      toast.success("Image saved successfully!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", {
        id: toastId,
      });
    } finally {
      setSavingImageRotation(false);
    }
  };

  const [showReassessModal, setShowReassessModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [features, setFeatures] = useState([]);
  const [filter, setFilter] = useState("all");
  const user = getUser();
  const userId = user?.id;
  const isInspector4 = user?.username === "inspector4";
  const showMLData = !isInspector4;
  const [starredPins, setStarredPins] = useState([]);
  const [starredLoading, setStarredLoading] = useState(false);

  const [showRefModal, setShowRefModal] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [pt6IssueDate, setPt6IssueDate] = useState("");
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [showNotReassessModal, setShowNotReassessModal] = useState(false);
  const [notReassessComment, setNotReassessComment] = useState("");
  const [autoOpenedPin, setAutoOpenedPin] = useState("");

  // Explanation state
  const [overallExplanation, setOverallExplanation] = useState("");
  const {
    showDuplicatePt5Modal,
    openDuplicatePt5Modal,
    closeDuplicatePt5Modal,
    saveDuplicatePt5Retry,
  } = useDuplicatePt5Retry();

  const applyNewPt5 = (newPt5) => {
    setReferenceNumber(newPt5);
    setFeatures((prev) =>
      prev.map((f) => (f.key === "pt" ? { ...f, after: newPt5 } : f))
    );
  };

  const [rowExplanations, setRowExplanations] = useState({
    floor: "",
    landuse: "",
    landarea: "",
    coveredarea: ""
  });

  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "null" && str !== "undefined";
  };

  const isReviewedMarker = (updatedBy) => {
    if (updatedBy === null || updatedBy === undefined) return false;
    const str = String(updatedBy).trim().toLowerCase();
    if (!str) return false;
    if (str === "null" || str === "undefined") return false;
    if (str === "0") return false;
    return true;
  };

  const normalizeId = (val) => String(val ?? "").trim();

  const isReassessYes = (val) =>
    val === 1 || val === "1" || val === true || String(val).toLowerCase() === "true";

  const isReassessNo = (val) =>
    val === 0 || val === "0" || val === false || String(val).toLowerCase() === "false";

  const formatWorkflowStatus = (raw) => {
    if (!hasUsableValue(raw)) return null;
    const val = String(raw).trim();
    const key = val.toLowerCase();
    const labels = {
      new: "New",
      resolved: "Resolved",
      flagged: "Flagged",
      assigned: "Assigned",
      explained: "Explained",
    };
    return labels[key] || val;
  };

  const getPrimaryStatusBadge = (property) => {
    if (!property) return null;

    const workflowKey = hasUsableValue(property.status)
      ? String(property.status).trim().toLowerCase()
      : "";

    if (workflowKey === "resolved") {
      return { key: "status", label: "Resolved", className: "bg-green-100 text-green-800" };
    }

    if (workflowKey === "flagged") {
      return { key: "status", label: "Flagged", className: "bg-yellow-100 text-yellow-800" };
    }

    const updatedBy = property.updated_by;
    const isReviewed = hasUsableValue(updatedBy);
    const isReviewedByYou = isReviewed && normalizeId(updatedBy) === normalizeId(userId);

    if (!isReviewedByYou) {
      return { key: "status", label: "Pending Review", className: "bg-blue-100 text-blue-800" };
    }

    if (isReassessYes(property.reassess)) {
      return { key: "status", label: "Reassessed", className: "bg-green-100 text-green-800" };
    }

    if (isReassessNo(property.reassess)) {
      return { key: "status", label: "Not Reassessed", className: "bg-red-100 text-red-800" };
    }

    return { key: "status", label: "Reviewed", className: "bg-slate-100 text-slate-700" };
  };

  const getStatusBadges = (property) => {
    const badge = getPrimaryStatusBadge(property);
    return badge ? [badge] : [];
  };

  const pickFirstValue = (property, keys) => {
    for (const key of keys) {
      const value = property?.[key];
      if (hasUsableValue(value)) return value;
    }
    return "";
  };

  const parseRange = (rangeStr) => {
    if (!rangeStr) return null;
    const str = String(rangeStr).toLowerCase().trim();

    // Remove any units (sq yards, sq yds, sq ft, sq.yds, sq.ft, etc.)
    const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

    // Check for "greater than X" or "more than X"
    let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'greater', value: Number(match[1]) };
    }

    // Check for "less than X" or "under X"
    match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'less', value: Number(match[1]) };
    }

    // Check for "between X and Y"
    match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return {
        type: 'between',
        min: Number(match[1]),
        max: Number(match[2])
      };
    }

    // Try to parse as a single number
    const num = Number(cleanStr);
    if (Number.isFinite(num)) {
      return { type: 'exact', value: num };
    }

    return null;
  };

  const areValuesDifferent = (adminVal, mlVal, field) => {
    const normalize = (val) => {
      if (val === null || val === undefined) return null;
      let raw = String(val).trim();
      if (!raw) return null;
      // Strip out units like "sq yards", "sq yds", "sq ft", "sq feet", "sq.yds", "sq.ft", etc. just like parseRange does
      raw = raw.replace(/sq\.?\s*(yards?|yds?|ft|feet?)/gi, '').replace(',', '').trim();
      const lowered = raw.toLowerCase();
      if (lowered === "n/a" || lowered === "null" || lowered === "undefined") return null;
      const numeric = Number(raw);
      if (Number.isFinite(numeric)) return numeric;
      return lowered;
    };

    // For landarea and coveredarea, check if admin is within ML range (only when comparing admin vs ML, not before vs after)
    if ((field === "landarea" || field === "land_area" || field === "coveredarea" || field === "covered_area") && mlVal) {
      const range = parseRange(mlVal);
      const a = normalize(adminVal);
      if (range && typeof a === "number") {
        switch (range.type) {
          case "greater":
            return a <= range.value; // highlight if admin is NOT greater than X
          case "less":
            return a >= range.value; // highlight if admin is NOT less than X
          case "between":
            return a < range.min || a > range.max; // highlight if strictly outside range (inclusive of min/max)
          case "exact":
            return Math.abs(a - range.value) > 1e-9; // highlight if different
        }
      }
    }

    // For all other fields (floor, land use), use direct comparison
    const a = normalize(adminVal);
    const m = normalize(mlVal);
    if (a === null || m === null) return false;
    if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
    return a !== m;
  };

  const hasReassessChanges = features
    .filter((f) => f.key !== "pt")
    .some(
      (f) =>
        f.after &&
        f.after.toString().trim() !== "" &&
        areValuesDifferent(f.before, f.after)
    );
  const hasReassessExplanation = overallExplanation.trim() !== "";
  const isReassessValid = hasReassessChanges && hasReassessExplanation;

  const getMLDisplayValue = (property, field) => {
    let realValue = "";
    switch (field) {
      case "floor":
        realValue = pickFirstValue(property, ["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
        break;
      case "landuse":
        realValue = pickFirstValue(property, ["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
        break;
      case "landarea":
        {
          const ml = pickFirstValue(property, ["landarea_ml", "land_area_ml"]);
          const bucket = pickFirstValue(property, ["landarea_combined_pred"]);
          const n = Number(String(ml ?? "").replaceAll(",", "").trim());
          const mlIsZeroish = Number.isFinite(n) && n <= 0;
          realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        }
        break;
      case "coveredarea":
        {
          const ml = pickFirstValue(property, ["coveredarea_ml", "covered_area_ml"]);
          const bucket = pickFirstValue(property, ["coveredarea_combined_pred"]);
          const n = Number(String(ml ?? "").replaceAll(",", "").trim());
          const mlIsZeroish = Number.isFinite(n) && n <= 0;
          realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        }
        break;
    }

    if (hasUsableValue(realValue)) {
      if (field === "landarea" || field === "coveredarea") {
        // Check if it's a range string first
        const strVal = String(realValue);
        const isRange = strVal.toLowerCase().includes("greater") ||
          strVal.toLowerCase().includes("less") ||
          strVal.toLowerCase().includes("between");
        if (isRange) {
          return realValue;
        }
        const n = Number(String(realValue).replaceAll(",", "").trim());
        if (Number.isFinite(n) && !Number.isNaN(n)) {
          return String(Math.round(n));
        }
        return realValue;
      }
      const num = Number(realValue);
      if (Number.isFinite(num) && !isNaN(num)) {
        return Number.isInteger(num) ? num : Math.round(num);
      }
      return realValue;
    }
    return 'N/A';
  };

  const toFiniteNumber = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    const n = Number(String(val).replaceAll(",", "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const formatTwoDecimals = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (!str) return "";
    const lowered = str.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return "";
    const n = toFiniteNumber(str);
    if (n === null) return str;
    return String(Math.round(n));
  };

  const inferLandAreaSqYards = (raw) => {
    const n = toFiniteNumber(raw);
    if (n === null || n < 0) return null;
    const rounded = Math.round(n);
    const isEffectivelyInteger = Math.abs(n - rounded) < 1e-6;
    if (n > 108000) return n / 9;
    if (n > 3000 && isEffectivelyInteger && rounded % 9 === 0) return n / 9;
    return n;
  };

  const inferCoveredAreaSqFt = (raw) => {
    const n = toFiniteNumber(raw);
    if (n === null || n < 0) return null;
    if (n <= 300) return n * 9;
    return n;
  };

  const GET_LAND_AREA_CATEGORY = (raw) => {
    const yards = inferLandAreaSqYards(raw);
    if (yards === null) return null;
    const cls =
      yards <= 31 ? 1 : yards <= 91 ? 2 : yards <= 175 ? 3 : yards <= 300 ? 4 : 5;
    const range =
      cls === 1
        ? "0–31 sq yards"
        : cls === 2
          ? "32–91 sq yards"
          : cls === 3
            ? "92–175 sq yards"
            : cls === 4
              ? "176–300 sq yards"
              : "301–108,000 sq yards";
    return { cls, range };
  };

  const GET_COVERED_AREA_CATEGORY = (raw) => {
    const sqft = inferCoveredAreaSqFt(raw);
    if (sqft === null) return null;
    const cls =
      sqft <= 400 ? 1 : sqft <= 977 ? 2 : sqft <= 1710 ? 3 : sqft <= 2920 ? 4 : 5;
    const range =
      cls === 1
        ? "0–400 sq ft"
        : cls === 2
          ? "401–977 sq ft"
          : cls === 3
            ? "978–1,710 sq ft"
            : cls === 4
              ? "1,711–2,920 sq ft"
              : "2,921–272,000 sq ft";
    return { cls, range };
  };

  // Load starred properties from API when component mounts
  useEffect(() => {
    if (!userId) return;
    const loadStarred = async () => {
      setStarredLoading(true);
      try {
        const res = await getStarredProperties();
        if (res.success) {
          setStarredPins(res.starredPins.map(normalizePin));
        }
      } catch (err) {
        console.error("❌ assignedai.jsx: error loading starred properties", err);
      } finally {
        setStarredLoading(false);
      }
    };
    loadStarred();
  }, [userId]);

  // Toggle star status
  // Toggle star status
  const toggleStar = async (pin) => {
    const normalizedPin = normalizePin(pin);
    if (!userId) {
      toast.error("User not logged in");
      return;
    }
    const wasStarred = starredPins.includes(normalizedPin);
    const actionName = wasStarred ? "unstar" : "star";

    // Track the star/unstar action with PIN
    await trackAction(actionName, normalizedPin, null, {
      star_action: actionName,
      property_id: normalizedPin
    });

    setStarredPins(prev =>
      wasStarred ? prev.filter(p => p !== normalizedPin) : [...prev, normalizedPin]
    );
    try {
      const res = await toggleStarProperty(normalizedPin);
      if (res.success && res.starredPins) {
        setStarredPins(res.starredPins.map(normalizePin));
      }
    } catch (err) {
      console.error("❌ assignedai.jsx: error toggling star", err);
      toast.error("Failed to update starred property");
      setStarredPins(prev =>
        wasStarred ? [...prev, normalizedPin] : prev.filter(p => p !== normalizedPin)
      );
    }
  };

  const isStarred = (pin) => starredPins.includes(normalizePin(pin));

  const fetchProperties = async () => {
    try {
      if (!userId) return setError("Inspector ID missing.");
      setLoading(true);
      const res = await axios.get(`/properties/assignments/inspector-notfiltered/${userId}`);
      const list = res.data || [];
      const seen = new Set();
      const dedupedList = list.filter((p) => {
        const key = normalizePin(p?.pin);
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const pendingOnly = dedupedList.filter((p) => {
        const workflow = String(p.status || "").trim().toLowerCase();
        const isResolved = workflow === "resolved";
        const isFlagged = workflow === "flagged";
        const isReviewed = isReassessYes(p.reassess) || isReassessNo(p.reassess);
        return !isResolved && !isFlagged && !isReviewed;
      });
      // setProperties(pendingOnly);
      // Stamp every fresh fetch with its own bust value so a page refresh
      // always forces a real network request for the current image bytes,
      // instead of trusting whatever the browser/CDN has cached at the plain URL.
      const fetchBust = Date.now();
      setProperties(pendingOnly.map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust })));
    } catch (err) {
      console.error(err);
      setError("Failed to load properties");
      toast.error("Failed to load assigned properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (!targetPinFromQuery || !properties.length) return;
    if (autoOpenedPin && autoOpenedPin === targetPinFromQuery) return;

    const matchedProperty = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === targetPinFromQuery.toLowerCase()
    );

    if (matchedProperty) {
      if (filter !== "all") setFilter("all");
      const idx = properties.findIndex(
        (p) => String(p.pin || "").trim().toLowerCase() === targetPinFromQuery.toLowerCase()
      );
      const targetPage = idx >= 0 ? Math.floor(idx / ITEMS_PER_PAGE) + 1 : 1;
      setPage(targetPage);
      setHighlightPin(normalizePin(matchedProperty.pin));
      setSelectedProperty(matchedProperty);
      setShowDetailModal(true);
      setAutoOpenedPin(targetPinFromQuery);
    }
  }, [targetPinFromQuery, properties, autoOpenedPin, filter]);

  useEffect(() => {
    if (!enablePortraitFix || !selectedProperty?.image) {
      setAutoRotation(0);
      return;
    }
    const img = new Image();
    img.onload = () => {
      setAutoRotation(img.naturalWidth > img.naturalHeight ? 90 : 0);
    };
    img.src = appendCacheBuster(
      resolveImageUrl(selectedProperty.image),
      selectedProperty?._imgBust
    );
    return () => {
      img.onload = null;
    };
  }, [enablePortraitFix, selectedProperty?.image, selectedProperty?._imgBust]);

  const filteredProperties = useMemo(() => {
    switch (filter) {
      case "starred":
        return properties.filter(prop => isStarred(prop.pin));
      case "unstarred":
        return properties.filter(prop => !isStarred(prop.pin));
      default:
        return properties;
    }
  }, [properties, filter, starredPins]);

  const totalPages = Math.ceil(filteredProperties.length / ITEMS_PER_PAGE);
  const paginated = filteredProperties.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const setFeaturesForReassess = (p) => {
    setOverallExplanation("");
    setRowExplanations({
      floor: "",
      landuse: "",
      landarea: "",
      coveredarea: ""
    });
    const existingRefno = p.refno || p.pt5 || p.pt_5 || p.pt || "";
    setReferenceNumber(existingRefno);
    setPt6IssueDate("");
    setFeatures([
      {
        key: "floor",
        label: "Floor",
        before: p.floor_admin || "",
        ml: getMLDisplayValue(p, "floor"),
        after: "",
        unit: ""
      },
      {
        key: "landuse",
        label: "Land Use",
        before: p.landuse_admin || "",
        ml: getMLDisplayValue(p, "landuse"),
        after: "",
        unit: ""
      },
      {
        key: "landarea",
        label: "Land Area",
        before: p.landarea_admin || "",
        ml: getMLDisplayValue(p, "landarea"),
        after: "",
        unit: "sq.ft"
      },
      {
        key: "coveredarea",
        label: "Covered Area",
        before: p.coveredarea_admin || "",
        ml: getMLDisplayValue(p, "coveredarea"),
        after: "",
        unit: "sq.ft"
      },
      {
        key: "pt",
        label: "PT-5 Serial Number",
        before: existingRefno || " ",
        ml: "",
        after: existingRefno || "",
        unit: ""
      },
    ]);
  };

  const updateFeature = (index, field, value) => {
    setFeatures((prev) => {
      const updated = [...prev];
      const f = updated[index];

      let validatedValue = value;
      if (field === "after") {
        const oldValStr = String(f.before || "").trim().toLowerCase();
        const newValStr = String(validatedValue || "").trim().toLowerCase();
        if (oldValStr !== "" && newValStr !== "" && oldValStr === newValStr) {
          toast.error("New value must be different from current value!");
          return prev;
        }

        switch (f.key) {
          case "floor":
          case "landarea":
          case "coveredarea":
            if (/[^0-9.]/.test(value)) {
              toast.error("Please enter only numbers for this field!");
              validatedValue = value.replace(/[^0-9.]/g, "");
            }
            if ((validatedValue.match(/\./g) || []).length > 1) {
              validatedValue = validatedValue.slice(0, -1);
            }
            if (validatedValue) {
              const numVal = parseFloat(validatedValue);
              if (f.key === "landarea") {
                if (numVal < 0 || numVal > 108000) {
                  toast.error("Land Area must be between 0 and 108,000 sq yards!");
                }
              } else if (f.key === "coveredarea") {
                if (numVal < 0 || numVal > 272000) {
                  toast.error("Covered Area must be between 0 and 272,000 sq ft!");
                }
              }
            }
            break;
          case "landuse":
            if (/[0-9]/.test(value)) {
              toast.error("Please enter only letters for Land Use!");
              validatedValue = value.replace(/[0-9]/g, "");
            }
            break;
        }
      }

      updated[index][field] = validatedValue;
      return updated;
    });
  };

  const updateRowExplanation = (key, value) => {
    setRowExplanations(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleNotReassess = async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    try {
      setSubmitting(true);

      const trimmed = String(notReassessComment || "").trim();
      await axios.post("/inspector/save-explanation", {
        property_id: selectedProperty.id,
        user_id: userId,
        overall_explanation: trimmed,
        row_explanations: {
          floor: "",
          landuse: "",
          landarea: "",
          coveredarea: ""
        }
      });

      const fd = new FormData();
      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "0");
      fd.append("comment", notReassessComment);
      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");
      fd.append("floors_ml", selectedProperty.floors_ml || "");
      fd.append("landuse_ml", selectedProperty.landuse_ml || "");
      fd.append("landarea_ml", selectedProperty.landarea_ml || "");
      fd.append("coveredarea_ml", selectedProperty.coveredarea_ml || "");
      fd.append("floors_pred", selectedProperty.floors_pred || "");
      fd.append("land_use_pred", selectedProperty.land_use_pred || "");
      fd.append("landarea_combined_pred", selectedProperty.landarea_combined_pred || "");
      fd.append("coveredarea_combined_pred", selectedProperty.coveredarea_combined_pred || "");
      fd.append("refno", selectedProperty.refno || "");

      await axios.put(`/properties/notfiltered-id/${selectedProperty.id}`, fd);

      await trackAction(
        "do_not_reassess",
        selectedProperty.pin,
        null,
        {
          comment_text: trimmed
            ? `Reason for not reassessing: ${trimmed}`
            : "No reason provided"
        }
      );

      toast.success("Successfully marked as 'Do Not Reassess'");
      setShowDetailModal(false);
      setShowNotReassessModal(false);
      setNotReassessComment("");
      fetchProperties();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const handleSubmitReassess = async ({ featuresOverride, refnoOverride, pt6IssueDateOverride } = {}) => {
    if (submitLockRef.current) {
      console.log('Submission already in progress, ignoring duplicate call');
      return;
    }
    submitLockRef.current = true;

    const activeFeatures = featuresOverride ?? features;
    const activePt6Date = pt6IssueDateOverride ?? pt6IssueDate;

    try {
      setSubmitting(true);

      await axios.post("/inspector/save-explanation", {
        property_id: selectedProperty.id,
        user_id: userId,
        overall_explanation: overallExplanation,
        row_explanations: rowExplanations
      });

      const fd = new FormData();
      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "1");
      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");
      fd.append("floors_ml", selectedProperty.floors_ml || "");
      fd.append("landuse_ml", selectedProperty.landuse_ml || "");
      fd.append("landarea_ml", selectedProperty.landarea_ml || "");
      fd.append("coveredarea_ml", selectedProperty.coveredarea_ml || "");

      const preds = {
        floors_pred: selectedProperty.floors_pred || "",
        land_use_pred: selectedProperty.land_use_pred || "",
        landarea_combined_pred: selectedProperty.landarea_combined_pred || "",
        coveredarea_combined_pred: selectedProperty.coveredarea_combined_pred || "",
        refno: selectedProperty.refno || ""
      };
      const inspectorAssessment = {
        inspector_floor: "",
        inspector_landuse: "",
        inspector_landarea: "",
        inspector_coveredarea: "",
      };

      activeFeatures.forEach((f) => {
        const val = f.key === "pt" && refnoOverride
          ? refnoOverride
          : f.after?.toString().trim();
        if (!val) return;
        switch (f.key) {
          case "floor": inspectorAssessment.inspector_floor = val; break;
          case "landuse": inspectorAssessment.inspector_landuse = val; break;
          case "landarea": inspectorAssessment.inspector_landarea = val; break;
          case "coveredarea": inspectorAssessment.inspector_coveredarea = val; break;
          case "pt": preds.refno = val; break;
        }
      });

      if (refnoOverride) {
        preds.refno = refnoOverride;
      }

      Object.entries(preds).forEach(([key, val]) => {
        fd.append(key, val);
      });
      Object.entries(inspectorAssessment).forEach(([key, val]) => {
        fd.append(key, val);
      });

      if (activePt6Date) {
        fd.append("pt6_issue_date", activePt6Date);
      }

      if (selectedProperty.exp_garv)
        fd.append("exp_garv", selectedProperty.exp_garv);

      await axios.put(`/properties/notfiltered-id/${selectedProperty.id}`, fd);

      // Extract individual explanations
      let floorExp = "";
      let landuseExp = "";
      let landareaExp = "";
      let coveredareaExp = "";

      // Check rowExplanations first
      if (rowExplanations) {
        floorExp = rowExplanations.floor || "";
        landuseExp = rowExplanations.landuse || "";
        landareaExp = rowExplanations.landarea || "";
        coveredareaExp = rowExplanations.coveredarea || "";
      }

      // Then check features for explanations
      activeFeatures.forEach(feature => {
        if (feature.explanation) {
          switch (feature.key) {
            case "floor": floorExp = feature.explanation; break;
            case "landuse": landuseExp = feature.explanation; break;
            case "landarea": landareaExp = feature.explanation; break;
            case "coveredarea": coveredareaExp = feature.explanation; break;
          }
        }
      });

      // Track each explanation as a separate row
      if (overallExplanation) {
        await trackAction(
          "Overall Explanation",
          selectedProperty.pin,
          null,
          {
            overall_explanation: overallExplanation
          }
        );
      }
      if (floorExp) {
        await trackAction(
          "Number of Floors Explanation",
          selectedProperty.pin,
          null,
          {
            floor_explanation: floorExp
          }
        );
      }
      if (landuseExp) {
        await trackAction(
          "Land Use Explanation",
          selectedProperty.pin,
          null,
          {
            landuse_explanation: landuseExp
          }
        );
      }
      if (landareaExp) {
        await trackAction(
          "Land Area Explanation",
          selectedProperty.pin,
          null,
          {
            landarea_explanation: landareaExp
          }
        );
      }
      if (coveredareaExp) {
        await trackAction(
          "Covered Area Explanation",
          selectedProperty.pin,
          null,
          {
            coveredarea_explanation: coveredareaExp
          }
        );
      }

      // Track each feature change separately (use for...of to preserve order)
      for (const feature of activeFeatures) {
        const oldVal = feature.before?.toString().trim() || "";
        const newVal = feature.after?.toString().trim() || "";
        if (oldVal !== newVal) {
          const featureName = FEATURE_LABELS[feature.key] || feature.label || feature.key;
          await trackAction(
            `${feature.key}_updated`,
            selectedProperty.pin,
            null,
            {
              old_value: oldVal,
              new_value: newVal,
              comment_text: `${featureName} changed from "${oldVal}" to "${newVal}"`
            }
          );
        }
      }

      await trackAction("reassessed", selectedProperty.pin);
      toast.success("Reassessment saved successfully!");
      setShowReassessModal(false);
      setShowRefModal(false);
      fetchProperties();
    } catch (err) {
      console.error(err);
      if (openDuplicatePt5Modal(err, async (newPt5) => {
        const updatedFeatures = activeFeatures.map((f) =>
          f.key === "pt" ? { ...f, after: newPt5 } : f
        );
        applyNewPt5(newPt5);
        await handleSubmitReassess({
          featuresOverride: updatedFeatures,
          refnoOverride: newPt5,
          pt6IssueDateOverride: activePt6Date,
        });
      })) {
        return;
      }
      toast.error(getPropertyUpdateErrorMessage(err));
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  useEffect(() => {
    if (!readyToSubmit || submitting) return;
    handleSubmitReassess();
    setReadyToSubmit(false);
  }, [readyToSubmit, submitting]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-4 md:p-6 overflow-y-auto">
        <Toaster position="top-right" />

        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Pending Review
              </h1>
              <p className="text-gray-600 mt-3 text-base italic border-l-4 border-blue-400 pl-4 bg-blue-50 py-2 rounded-r">
                Hello!<br />
                These are the properties that have been shortlisted for your review. Please go through each property and decide whether you want to reassess each or not.
              </p>
              <p className="text-gray-600 mt-2 text-lg font-bold">
                Total: <span className="font-bold text-xl">{properties.length}</span> properties
                {filter !== "all" && (
                  <>
                    {" • "}
                    <span className="font-semibold">{filteredProperties.length}</span> {filter} properties
                  </>
                )}
              </p>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
              <div className="flex items-center gap-2 bg-white p-2 rounded-lg shadow">
                <button
                  onClick={() => setFilter("all")}
                  className={`px-3 py-1.5 text-sm rounded transition-colors ${filter === "all"
                    ? "bg-blue-100 text-blue-700 font-medium"
                    : "text-gray-600 hover:bg-gray-100"
                    }`}
                  data-click-id="pending_review_all"
                >
                  All
                </button>
                <button
                  onClick={() => setFilter("starred")}
                  className={`px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-1.5 ${filter === "starred"
                    ? "bg-yellow-100 text-yellow-700 font-medium"
                    : "text-gray-600 hover:bg-gray-100"
                    }`}
                  data-click-id="pending_review_starred"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Starred ({properties.filter(p => starredPins.includes(normalizePin(p.pin))).length})
                </button>
                <button
                  onClick={() => setFilter("unstarred")}
                  className={`px-3 py-1.5 text-sm rounded transition-colors ${filter === "unstarred"
                    ? "bg-gray-100 text-gray-700 font-medium"
                    : "text-gray-600 hover:bg-gray-100"
                    }`}
                  data-click-id="pending_review_unstarred"
                >
                  Unstarred
                </button>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600 bg-white px-4 py-2 rounded-lg shadow">
                  Showing <span className="font-semibold">{paginated.length}</span> of{" "}
                  <span className="font-semibold">{filteredProperties.length}</span> properties
                </div>

                <button
                  onClick={fetchProperties}
                  disabled={loading}
                  className="p-2 bg-white hover:bg-gray-100 text-gray-600 rounded-lg shadow transition-colors disabled:opacity-50"
                  title="Refresh"
                  data-click-id="pending_review_refresh"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading assigned properties...</p>
            </div>
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-semibold text-red-800">Error Loading Properties</h3>
            </div>
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchProperties}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && filteredProperties.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow">
            <div className="text-6xl mb-6">
              {filter === "starred" ? "⭐" : "🏠"}
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {filter === "starred"
                ? "No Starred Properties"
                : filter === "unstarred"
                  ? "No Unstarred Properties"
                  : "No Properties Assigned"}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {filter === "starred"
                ? "Star properties by clicking the star icon on property cards."
                : filter === "unstarred"
                  ? "All properties are currently starred. Try viewing all properties."
                  : "You don't have any properties assigned to you yet."}
            </p>
            {filter !== "all" && (
              <button
                onClick={() => setFilter("all")}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                View All Properties
              </button>
            )}
          </div>
        )}

        {!loading && !error && filteredProperties.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginated.map((prop) => (
                <MOTION.div
                  key={prop.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  data-pin={normalizePin(prop.pin)}
                  className={`bg-white shadow hover:shadow-lg overflow-hidden group relative transition-all rounded-xl ${normalizePin(prop.pin) && normalizePin(prop.pin) === highlightPin
                    ? "border-2 border-sky-500"
                    : "border border-gray-200"
                    }`}
                >
                  {normalizePin(prop.pin) && normalizePin(prop.pin) === highlightPin && (
                    <div className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold shadow">
                      ✓
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStar(prop.pin);
                    }}
                    className="absolute top-3 right-3 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow hover:bg-white transition-colors"
                    title={isStarred(prop.pin) ? "Unstar property" : "Star property"}
                  >
                    <svg
                      className={`w-5 h-5 transition-transform duration-300 ${isStarred(prop.pin)
                        ? "fill-yellow-500 text-yellow-500"
                        : "fill-gray-300 text-gray-300 hover:fill-yellow-300 hover:text-yellow-300"
                        }`}
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </button>

                  <div
                    className="h-48 bg-gray-100 overflow-hidden cursor-pointer flex items-center justify-center"
                    onClick={() => {
                      setSelectedProperty(prop);
                      setShowDetailModal(true);
                    }}
                  >
                    {prop.image ? (
                      <div className="h-full w-full transition-transform duration-300 group-hover:scale-105">
                        <PortraitImage
                          src={appendCacheBuster(resolveImageUrl(prop.image), prop._imgBust)}
                          alt="Property"
                          enablePortrait={enablePortraitFix}
                          enableExif={enableExifOrientationFix}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gray-50">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-lg text-gray-900 truncate pr-2">
                        {prop.pin}
                      </h3>
                      {isStarred(prop.pin) && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Starred
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        <span>Circle: {prop.circle || "N/A"}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => {
                          setSelectedProperty(prop);
                          setShowDetailModal(true);
                          trackAction("pending_review_view_details", prop.pin);
                        }}
                        className="w-full py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors flex items-center justify-center gap-1"
                        data-click-id="pending_review_view_details"
                        data-property-id={prop.pin}
                      >
                        View Details
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </MOTION.div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <div className="flex items-center gap-2 bg-white p-4 rounded-xl shadow">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50"
                    data-click-id="pending_review_page_previous"
                  >
                    Previous
                  </button>

                  <span className="px-4 text-sm text-gray-600">
                    Page <span className="font-semibold">{page}</span> of <span className="font-semibold">{totalPages}</span>
                  </span>

                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50"
                    data-click-id="pending_review_page_next"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* DETAIL MODAL - Full screen with no scroll (same style as resolved page) */}
        <AnimatePresence>
          {showDetailModal && selectedProperty && (
            <MOTION.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
              onClick={closeDetailModal}
            >
              <MOTION.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white w-full h-full shadow-2xl p-6 relative overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={closeDetailModal}
                  className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-10 flex items-center justify-center w-10 h-10"
                  data-click-id="reviewed_close_details"
                  data-property-id={selectedProperty.pin}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Header Info */}
                <div className="mb-6 pr-14">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-gray-500 text-sm">Circle</p>
                      <p className="text-lg font-bold text-gray-800 uppercase">{selectedProperty.circle || "N/A"}</p>
                    </div>
                    <div className="text-right">
                      {getStatusBadges(selectedProperty).map((badge) => (
                        <span
                          key={badge.key}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${badge.className} ml-2`}
                        >
                          {badge.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-500">
                    <div>
                      <p>
                        PIN: <span className="font-semibold text-gray-700">{selectedProperty.pin}</span>
                      </p>
                      {/* <p className="mt-1">
                        PT-5 Reference:{" "}
                        <span className="font-semibold text-gray-700">
                          {pickFirstValue(selectedProperty, ["refno", "pt5", "pt_5", "pt"]) || "N/A"}
                        </span>
                      </p> */}
                    </div>
                    <button
                      onClick={() => toggleStar(selectedProperty.pin)}
                      className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                      title={isStarred(selectedProperty.pin) ? "Unstar property" : "Star property"}
                    >
                      <svg
                        className={`w-5 h-5 ${isStarred(selectedProperty.pin)
                          ? "fill-yellow-500 text-yellow-500"
                          : "fill-gray-400 text-gray-400 hover:fill-yellow-400 hover:text-yellow-400"
                          }`}
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* IMAGE and Info Section - Side by side layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Image Section */}
                  <div className="relative group bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer h-80">
                    {selectedProperty.image ? (
                      <>
                        <div
                          className="w-full h-full transition-transform duration-300 ease-in-out flex items-center justify-center"
                          style={{ transform: `rotate(${displayedRotation}deg)` }}
                          onClick={() => selectedProperty.image && setShowFullImage(true)}
                        >
                          <img
                            src={appendCacheBuster(
                              resolveImageUrl(selectedProperty.image),
                              selectedProperty?._imgBust
                            )}
                            style={
                              enableExifOrientationFix
                                ? { imageOrientation: "from-image" }
                                : undefined
                            }
                            className="max-w-full max-h-full object-contain hover:opacity-90 transition-opacity"
                            alt="Property"
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                          <span className="text-white bg-black/50 px-3 py-1 rounded-full text-sm font-medium">Click to Enlarge</span>
                        </div>
                        <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                          <button
                            onClick={handleRotate}
                            disabled={savingImageRotation}
                            className="p-3 bg-black/50 hover:bg-black/70 disabled:opacity-60 rounded-full text-white transition-all shadow-lg"
                            title="Rotate Image"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                          {(rotation !== 0 || autoRotation !== 0) && (
                            <button
                              onClick={handleSaveRotation}
                              disabled={savingImageRotation}
                              className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                              title="Save Rotation"
                            >
                              {savingImageRotation ? (
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <>
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-60 bg-gray-200 flex items-center justify-center rounded-lg text-gray-500">
                        No Image
                      </div>
                    )}
                  </div>

                  {/* Property Information Table */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-800">Property Information</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border rounded-lg overflow-hidden text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="p-2 text-left font-medium">Feature</th>
                            <th className="p-2 text-left font-medium">Admin</th>
                            {showMLData && (
                              <th className="p-2 text-left font-medium">Computer Vision</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className={`border-b ${showMLData && areValuesDifferent(selectedProperty.floor_admin, getMLDisplayValue(selectedProperty, 'floor'), 'floor') ? 'bg-red-100' : ''}`}>
                            <td className="p-2 font-medium">Floor</td>
                            <td className="p-2">{hasUsableValue(selectedProperty.floor_admin) ? selectedProperty.floor_admin : "N/A"}</td>
                            {showMLData && (
                              <td className="p-2">{getMLDisplayValue(selectedProperty, 'floor')}</td>
                            )}
                          </tr>

                          <tr className={`border-b ${showMLData && areValuesDifferent(selectedProperty.landuse_admin, getMLDisplayValue(selectedProperty, 'landuse'), 'landuse') ? 'bg-red-100' : ''}`}>
                            <td className="p-2 font-medium">Land Use</td>
                            <td className="p-2">{hasUsableValue(selectedProperty.landuse_admin) ? selectedProperty.landuse_admin : "N/A"}</td>
                            {showMLData && (
                              <td className="p-2">{getMLDisplayValue(selectedProperty, 'landuse')}</td>
                            )}
                          </tr>

                          <tr className={`border-b ${showMLData && areValuesDifferent(selectedProperty.landarea_admin, getMLDisplayValue(selectedProperty, 'landarea'), 'landarea') ? 'bg-red-100' : ''}`}>
                            <td className="p-2 font-medium">Land Area</td>
                            <td className="p-2">
                              {hasUsableValue(selectedProperty.landarea_admin)
                                ? `${formatTwoDecimals(selectedProperty.landarea_admin) || selectedProperty.landarea_admin} sq.yards`
                                : "N/A"}
                            </td>
                            {showMLData && (
                              <td className="p-2">
                                {/* {getMLDisplayValue(selectedProperty, 'landarea')} */}
                              </td>
                            )}
                          </tr>

                          <tr className={`border-b ${showMLData && areValuesDifferent(selectedProperty.coveredarea_admin, getMLDisplayValue(selectedProperty, 'coveredarea'), 'coveredarea') ? 'bg-red-100' : ''}`}>
                            <td className="p-2 font-medium">Covered Area</td>
                            <td className="p-2">
                              {hasUsableValue(selectedProperty.coveredarea_admin)
                                ? `${formatTwoDecimals(selectedProperty.coveredarea_admin) || selectedProperty.coveredarea_admin} sq.ft`
                                : "N/A"}
                            </td>
                            {showMLData && (
                              <td className="p-2">
                                {/* {getMLDisplayValue(selectedProperty, 'coveredarea')} */}
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {(() => {
                  const hasInspectorDecision =
                    (selectedProperty.reassess === 0 || selectedProperty.reassess === 1 ||
                      selectedProperty.reassess === "0" || selectedProperty.reassess === "1" ||
                      selectedProperty.reassess === true || selectedProperty.reassess === false);
                  const isSupervisorDecisionMade = selectedProperty.status === "resolved";

                  if (isSupervisorDecisionMade) {
                    return (
                      <div className="text-center py-6 border-t mt-4">
                        <p className="text-gray-500">Supervisor has already made a decision.</p>
                        <p className="text-sm text-gray-400">Cannot edit final decision.</p>
                      </div>
                    );
                  }

                  if (hasInspectorDecision) {
                    return (
                      <div className="flex gap-3 pt-4 border-t mt-4">
                        <button
                          onClick={() => {
                            setNotReassessComment("");
                            setShowDetailModal(false);
                            setShowNotReassessModal(true);
                          }}
                          disabled={submitting}
                          className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                          data-click-id="reviewed_change_to_do_not_reassess"
                          data-property-id={selectedProperty.pin}
                        >
                          Edit: Do Not Reassess
                        </button>

                        <button
                          onClick={() => {
                            setFeaturesForReassess(selectedProperty);
                            setShowDetailModal(false);
                            setShowReassessModal(true);
                          }}
                          disabled={submitting}
                          className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                          data-click-id="reviewed_edit"
                          data-property-id={selectedProperty.pin}
                        >
                          Edit: Reassess
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div className="flex gap-3 pt-4 border-t mt-4">
                      <button
                        onClick={() => {
                          setNotReassessComment("");
                          setShowDetailModal(false);
                          setShowNotReassessModal(true);
                        }}
                        disabled={submitting}
                        className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                        data-click-id="do_not_reassess"
                        data-property-id={selectedProperty.pin}
                      >
                        Do Not Reassess
                      </button>

                      <button
                        onClick={() => {
                          setFeaturesForReassess(selectedProperty);
                          setShowDetailModal(false);
                          setShowReassessModal(true);
                        }}
                        disabled={submitting}
                        className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                        data-click-id="reassess"
                        data-property-id={selectedProperty.pin}
                      >
                        Reassess
                      </button>
                    </div>
                  );
                })()}
              </MOTION.div>
            </MOTION.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showReassessModal && (
            <MOTION.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
            >
              <MOTION.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white w-full h-full shadow-xl overflow-y-auto p-6"
              >
                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  Reassessment
                </h2>

                <div className="overflow-x-auto mb-6">
                  <table className="w-full border">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-3 text-left font-medium">Feature</th>
                        <th className="p-3 text-left font-medium">Current (Admin)</th>
                        <th className="p-3 text-left font-medium">Computer Vision Prediction</th>
                        <th className="p-3 text-left font-medium">Updated </th>
                        <th className="p-3 text-left font-medium">Explanation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {features.filter((f) => f.key !== "pt").map((f) => {
                        const featureIndex = features.findIndex((x) => x.key === f.key);
                        return (
                          <tr
                            key={f.key}
                            className={`border-b ${showMLData && areValuesDifferent(f.before, f.ml, f.key) ? 'bg-red-100' : ''
                              }`}
                          >
                            <td className="p-3 font-medium">{f.label}</td>
                            <td className="p-3">
                              <div className="bg-gray-50 p-2 rounded">
                                {f.key === "landarea" || f.key === "coveredarea"
                                  ? (formatTwoDecimals(f.before) || "Not set")
                                  : (f.before || "Not set")}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="bg-gray-50 p-2 rounded">
                                {f.key === "landarea" || f.key === "coveredarea"
                                  ? (formatTwoDecimals(f.ml) || f.ml || "N/A")
                                  : (f.ml || "N/A")}
                              </div>
                            </td>
                            <td className="p-3">
                              {f.key === "landuse" ? (
                                <select
                                  value={f.after || ""}
                                  onChange={(e) => updateFeature(featureIndex, "after", e.target.value)}
                                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="">Select Land Use</option>
                                  <option value="Residential">Residential</option>
                                  <option value="Mixed">Mixed</option>
                                  <option value="Commercial">Commercial</option>
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={f.after || ""}
                                  onChange={(e) => updateFeature(featureIndex, "after", e.target.value)}
                                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder={`Enter ${f.label.toLowerCase()}...`}
                                />
                              )}
                            </td>
                            <td className="p-3">
                              <input
                                className="w-full p-2 border rounded"
                                placeholder={`Explain ${f.label.toLowerCase()} change...`}
                                value={rowExplanations[f.key] || ""}
                                onChange={e => updateRowExplanation(f.key, e.target.value)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* PT-5 Serial Number */}
                <div className="mb-6">
                  <label className="block font-medium text-gray-700 mb-2">
                    PT-5 Serial Number <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={referenceNumber}
                    onChange={(e) => {
                      const val = e.target.value;
                      setReferenceNumber(val);
                      setFeatures((prev) =>
                        prev.map((f) => (f.key === "pt" ? { ...f, after: val } : f))
                      );
                    }}
                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter PT-5 Serial Number ..."
                  />
                </div>

                {/* Overall Explanation */}
                <div className="mb-6">
                  <label className="block font-medium text-gray-700 mb-2">Overall Explanation</label>
                  <textarea
                    className="w-full border p-3 rounded-lg"
                    rows={4}
                    placeholder="Provide an overall explanation for the reassessment..."
                    value={overallExplanation}
                    onChange={e => setOverallExplanation(e.target.value)}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      if (!hasReassessExplanation) {
                        toast.error("Please enter explanation of reassessment");
                        return;
                      }
                      if (!hasReassessChanges) {
                        toast.error("Please update at least one feature");
                        return;
                      }
                      const ptFeature = features.find(f => f.key === "pt");
                      if (ptFeature?.after) {
                        setReferenceNumber(ptFeature.after);
                      }
                      setShowReassessModal(false);
                      setShowRefModal(true);
                    }}
                    aria-disabled={!isReassessValid || submitting}
                    disabled={submitting}
                    className={`flex-1 px-4 py-2 rounded text-white
                      ${isReassessValid
                        ? "bg-green-600 hover:bg-green-700"
                        : "bg-green-300 cursor-not-allowed"
                      }`}
                  >
                    Next
                  </button>

                  <button
                    onClick={() => {
                      setShowReassessModal(false);
                      setShowDetailModal(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                    data-click-id="reassessment canceled"
                    data-property-id={selectedProperty.pin}
                  >
                    Cancel
                  </button>
                </div>
              </MOTION.div>
            </MOTION.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showNotReassessModal && (
            <MOTION.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <MOTION.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
              >
                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  Reason for Not Reassessing
                </h2>

                <textarea
                  value={notReassessComment}
                  onChange={(e) => setNotReassessComment(e.target.value)}
                  rows={4}
                  placeholder="Enter reason / comments..."
                  className="w-full p-3 border rounded mb-3"
                />

                {!notReassessComment && (
                  <p className="text-red-500 text-sm mb-3">
                    Comment is required
                  </p>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleNotReassess}
                    disabled={!notReassessComment || submitting}
                    className={`flex-1 px-4 py-2 rounded text-white
                      ${notReassessComment
                        ? "bg-gray-700 hover:bg-gray-800"
                        : "bg-gray-400 cursor-not-allowed"
                      }`}
                    data-click-id="reason_for_not_reassessing_confirm"
                    data-property-id={selectedProperty.pin}
                  >
                    {submitting ? "Saving..." : "Confirm"}
                  </button>

                  <button
                    onClick={() => {
                      setShowNotReassessModal(false);
                      setShowDetailModal(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                    data-click-id="reason_for_not_reassessing_cancel"
                    data-property-id={selectedProperty.pin}
                  >
                    Cancel
                  </button>
                </div>
              </MOTION.div>
            </MOTION.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          <DuplicatePt5Modal
            isOpen={showDuplicatePt5Modal}
            onClose={closeDuplicatePt5Modal}
            onSave={saveDuplicatePt5Retry}
            submitting={submitting}
          />

          {showRefModal && (
            <MOTION.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <MOTION.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
              >
                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  {pt6IssueDate ? "Edit PT-6 Issue Date" : "Enter PT-6 Issue Date"}
                </h2>

                {referenceNumber && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">PT-5 Serial:</span> {referenceNumber}
                    </p>
                  </div>
                )}

                <label className="block font-medium text-gray-700 mb-2">
                  PT-6 Issue Date <span className="text-gray-400">*</span>
                </label>
                <input
                  type="date"
                  value={pt6IssueDate}
                  onChange={(e) => setPt6IssueDate(e.target.value)}
                  className="w-full p-3 border rounded mb-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-sm text-gray-500 mb-4">
                  Date when PT-6 was issued
                </p>

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={async () => {
                      // Validate PT-6 date
                      if (!pt6IssueDate) {
                        toast.error("PT-6 Issue Date is mandatory.");
                        return;
                      }
                      const finalPt5 = features.find(f => f.key === "pt")?.after || referenceNumber;
                      setFeatures(prev =>
                        prev.map(f =>
                          f.key === "pt" ? { ...f, after: finalPt5 } : f
                        )
                      );
                      setReferenceNumber(finalPt5);

                      await trackAction(
                        "pt6_save",
                        selectedProperty?.pin,
                        null,
                        {
                          comment_text: `PT-6 Issue Date set to "${pt6IssueDate}"`
                        }
                      );

                      setShowRefModal(false);
                      setReadyToSubmit(true);
                    }}
                    disabled={submitting}
                    className={`flex-1 px-4 py-2 rounded text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed`}
                    data-click-id="pt6_save"
                    data-property-id={selectedProperty?.pin}
                  >
                    {submitting ? "Saving..." : "Save & Submit"}
                  </button>

                  <button
                    onClick={() => {
                      setShowRefModal(false);
                      setShowReassessModal(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                    data-click-id="pt6_cancel"
                    data-property-id={selectedProperty?.pin}
                  >
                    Back
                  </button>
                </div>
              </MOTION.div>
            </MOTION.div>
          )}
        </AnimatePresence>

        {/* Fullscreen Image Modal */}
        <AnimatePresence>
          {showFullImage && selectedProperty && selectedProperty.image && (
            <MOTION.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
              onClick={() => {
                setShowFullImage(false);
                setRotation(0);
                setAutoRotation(0);
              }}
            >
              <div className="absolute top-6 right-6 flex space-x-4 z-10">
                <button
                  onClick={handleRotate}
                  disabled={savingImageRotation}
                  className="p-3 bg-white/10 hover:bg-white/20 disabled:opacity-60 rounded-full transition-all shadow-lg flex items-center justify-center"
                  title="Rotate Image"
                  data-click-id="pending_review_image_rotate"
                  data-property-id={selectedProperty.pin}
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                {(rotation !== 0 || autoRotation !== 0) && (
                  <button
                    onClick={handleSaveRotation}
                    disabled={savingImageRotation}
                    className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                    title="Save Rotation"
                  >
                    {savingImageRotation ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowFullImage(false);
                    setRotation(0);
                    setAutoRotation(0);
                  }}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center text-white text-2xl"
                  data-click-id="pending_review_image_close"
                  data-property-id={selectedProperty.pin}
                >
                  ✕
                </button>
              </div>
              <MOTION.div
                initial={{ scale: 0.9, rotate: 0 }}
                animate={{ scale: 1, rotate: displayedRotation }}
                exit={{ scale: 0.9 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="relative"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={appendCacheBuster(
                    resolveImageUrl(selectedProperty.image),
                    selectedProperty?._imgBust
                  )}
                  style={
                    enableExifOrientationFix
                      ? { imageOrientation: "from-image" }
                      : undefined
                  }
                  className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
                  alt="Full Property"
                />
              </MOTION.div>
            </MOTION.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default Assigned;
